import React, { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { DRBFMSession } from '../../../types';
import { canOpenSession } from './sessionNavigation';
import SessionRowActions from './SessionRowActions';
import { getStatusLabel, getStatusVariant } from './sessionStatus';
import { useSessionDateFormat } from './useSessionDateFormat';

interface SessionsTableProps {
  sessions: DRBFMSession[];
  view3DSessionId: string | null;
  exportingSessionId: string | null;
  isConvertBusy: boolean;
  onView: (session: DRBFMSession) => void;
  onView3D: (session: DRBFMSession) => void;
  onExport: (session: DRBFMSession) => void;
  onConvert: (session: DRBFMSession) => void;
  onDelete: (session: DRBFMSession) => void;
}

type RowActionsProps = Omit<SessionsTableProps, 'sessions'>;

const RowActionsContext = createContext<RowActionsProps | null>(null);

// Module-level so flexRender keeps one component type and never re-mounts the buttons.
function ActionsCell({ row }: CellContext<DRBFMSession, unknown>) {
  const props = useContext(RowActionsContext);
  if (!props) throw new Error('ActionsCell needs RowActionsContext');
  const { view3DSessionId, exportingSessionId, ...actions } = props;
  return (
    // Row click opens the session; the action buttons must not also trigger it.
    <div onClick={(event) => event.stopPropagation()}>
      <SessionRowActions
        session={row.original}
        isLoadingView3D={view3DSessionId === row.original.id}
        isExporting={exportingSessionId === row.original.id}
        {...actions}
      />
    </div>
  );
}

const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, ...rowActions }) => {
  const { t } = useTranslation();
  const formatDate = useSessionDateFormat();

  const columns = useMemo<ColumnDef<DRBFMSession, any>[]>(
    () => [
      {
        id: 'analysis',
        accessorFn: (session) => session.title?.trim() || session.itemName,
        header: t('drbfm.history.columnAnalysis', '分析対象'),
        cell: ({ row }) => (
          // Primary label: title when present, else itemName. The title input is
          // optional and most sessions leave it blank, so showing two separate
          // columns gave us a dead "-" column. Subtitle shows itemName whenever
          // title is also present, so the user keeps both.
          <>
            <p className="revy-cjk text-sm font-medium leading-[1.5]">
              {row.original.title?.trim() || row.original.itemName}
            </p>
            {row.original.title?.trim() &&
              row.original.title.trim() !== row.original.itemName && (
                <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
                  {row.original.itemName}
                </p>
              )}
          </>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('drbfm.history.columnStatus', 'ステータス'),
        cell: ({ row }) => (
          <Badge variant={getStatusVariant(row.original.status)} className="revy-cjk">
            {getStatusLabel(row.original.status, t)}
          </Badge>
        ),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('drbfm.history.columnCreated', '作成日時'),
        cell: ({ row }) => (
          <span className="revy-figure whitespace-nowrap text-sm text-muted-foreground">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'completedAt',
        accessorKey: 'completedAt',
        header: t('drbfm.history.columnCompleted', '完了日時'),
        cell: ({ row }) => (
          <span className="revy-figure whitespace-nowrap text-sm text-muted-foreground">
            {formatDate(row.original.completedAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => (
          <span className="block text-right">
            {t('drbfm.history.columnActions', '操作')}
          </span>
        ),
        enableSorting: false,
        cell: ActionsCell,
      },
    ],
    [t, formatDate],
  );

  return (
    <RowActionsContext.Provider value={rowActions}>
      <DataTable
        paginated
        columns={columns}
        data={sessions}
        getRowId={(row) => row.id}
        onRowClick={(session) => {
          if (canOpenSession(session)) rowActions.onView(session);
        }}
      />
    </RowActionsContext.Provider>
  );
};

export default SessionsTable;
